<template>
  <router-link v-bind="$attrs" :to="to" @pointerenter="enter" @pointerleave="hide"
    @focus="focus" @blur="hide" @click="dismiss"><slot/></router-link>
  <Teleport to="body">
    <Transition name="home-page-preview">
      <div v-if="open" ref="preview" class="home-page-preview" :style="position"
        @pointerenter="keep" @pointerleave="hide" @focusin="keep" @focusout="hide" @keydown.esc="dismiss">
        <div class="home-page-preview-heading"><span>{{ label }}</span><router-link :to="to" @click="dismiss" :aria-label="`Open ${label}`">Open<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 10h13m-5-5 5 5-5 5"/></svg></router-link></div>
        <iframe :src="`#${to}?preview=1&surface=glass`" :title="`${label} preview`" loading="lazy" tabindex="-1"/>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { supportsDesktopHover, watchDesktopHover } from '../../utils/desktopHover';
import { previewDelay, warmPreviewIntent } from '../../utils/previewIntent';
const PREVIEW_HOVER = { minWidth: 0 };
export default {
  name: 'HomePreviewLink',
  inheritAttrs: false,
  props: { to: { type: String, required: true }, label: { type: String, required: true } },
  data: () => ({ open: false, position: null }),
  mounted() {
    this._stopHover = watchDesktopHover(enabled => { if (!enabled) this.dismiss(); }, PREVIEW_HOVER);
    this._dismiss = () => this.dismiss();
    this._escape = event => { if (event.key === 'Escape') this.dismiss(); };
    window.addEventListener('scroll', this._dismiss, { passive: true });
    window.addEventListener('resize', this._dismiss, { passive: true });
    window.addEventListener('keydown', this._escape);
  },
  beforeUnmount() {
    this.dismiss();
    this._stopHover?.();
    window.removeEventListener('scroll', this._dismiss);
    window.removeEventListener('resize', this._dismiss);
    window.removeEventListener('keydown', this._escape);
  },
  deactivated() { this.dismiss(); },
  methods: {
    enter(event) { if (event.pointerType !== 'touch' && supportsDesktopHover(PREVIEW_HOVER)) { this.keep();const host=event.currentTarget;this._show=setTimeout(()=>this.show(host),previewDelay()); } },
    focus(event) { if (supportsDesktopHover(PREVIEW_HOVER) && event.target.matches(':focus-visible')) this.show(event.currentTarget); },
    show(host) {
      this.keep();
      const rect = host.getBoundingClientRect();
      const width = Math.min(430, innerWidth - 32);
      const overlap = Math.min(64, width * .14);
      const leftMin = Math.max(16, rect.left + overlap - width);
      const leftMax = Math.min(innerWidth - width - 16, rect.right - overlap);
      const left = leftMin <= leftMax ? Math.max(leftMin, Math.min((innerWidth - width) / 2, leftMax)) : Math.max(16, Math.min(rect.left, innerWidth - width - 16));
      this.position = { left: `${left}px`, top: `${rect.bottom + 12}px`, visibility: 'hidden' };
      this.open = true;
      warmPreviewIntent();
      this.$nextTick(() => {
        if (!this.$refs.preview) return;
        const height = this.$refs.preview.offsetHeight;
        const headerBottom = document.querySelector('.header')?.getBoundingClientRect().bottom || 0;
        const topInset = Math.max(8, headerBottom < innerHeight / 3 ? headerBottom + 8 : 8);
        const aboveSpace = Math.max(0, rect.top - topInset - 12);
        const belowSpace = Math.max(0, innerHeight - rect.bottom - 20);
        const below = belowSpace >= height || belowSpace >= aboveSpace;
        const available = below ? belowSpace : aboveSpace;
        const top = below ? rect.bottom + 12 : rect.top - Math.min(height, available) - 12;
        this.position = {
          left: `${left}px`, top: `${top}px`,
          '--preview-content-height': `${Math.max(0, available - 53)}px`,
          '--preview-origin': `${Math.max(0, Math.min(width, rect.left + rect.width / 2 - left))}px ${below ? 'top' : 'bottom'}`,
          '--preview-offset': below ? '-5px' : '5px',
        };
      });
    },
    keep() { clearTimeout(this._hide); clearTimeout(this._show); },
    hide() { this.keep(); if(this.open)warmPreviewIntent(); this._hide = setTimeout(() => { this.open = false; }, 160); },
    dismiss() { this.keep(); this.open = false; },
  },
};
</script>

<style scoped>
.home-page-preview {
  --preview-tint: rgba(255, 255, 255, .66);
  --preview-solid: #fafafa;
  --preview-ink: #333;
  --preview-muted: #626262;
  --preview-edge: rgba(255, 255, 255, .8);
  --preview-outline: rgba(30, 30, 30, .13);
  --preview-hover: rgba(0, 0, 0, .055);
  position: fixed;
  z-index: 1000;
  box-sizing: border-box;
  width: min(430px, calc(100vw - 32px));
  padding: 0 5px 5px;
  border: 1px solid var(--preview-edge);
  border-radius: 16px;
  background: var(--preview-solid);
  box-shadow: 0 0 0 1px var(--preview-outline), 0 24px 60px -16px rgba(0, 0, 0, .22), 0 4px 12px -4px rgba(0, 0, 0, .09), inset 0 -1px 0 rgba(255, 255, 255, .25);
  overflow: hidden;
  transform-origin: var(--preview-origin, center);
}
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .home-page-preview { background: transparent; }
  .home-page-preview::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    clip-path: inset(0 round 15px);
    pointer-events: none;
    background: var(--preview-tint);
    -webkit-backdrop-filter: blur(24px) saturate(.8);
    backdrop-filter: blur(24px) saturate(.8);
  }
}
.home-page-preview-heading {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 46px;
  padding: 0 10px 0 13px;
  color: var(--preview-ink);
  font: 600 .75rem/1.4 'Quattrocento Sans', sans-serif;
  letter-spacing: .02em;
}
.home-page-preview-heading a {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 8px;
  border-radius: 6px;
  color: var(--preview-muted);
  text-decoration: none;
  font-weight: 400;
  cursor: var(--site-interactive-cursor);
  transition: color 160ms ease, background-color 160ms ease;
}
.home-page-preview-heading svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.2; transition: transform 200ms ease; }
.home-page-preview-heading a:hover,
.home-page-preview-heading a:focus-visible { color: var(--preview-ink); background: var(--preview-hover); }
.home-page-preview-heading a:focus-visible { outline: 1px solid currentColor; outline-offset: 2px; }
.home-page-preview-heading a:hover svg { transform: translateX(2px); }
.home-page-preview iframe {
  position: relative;
  display: block;
  width: 100%;
  height: min(340px, calc(100svh - 116px));
  max-height: var(--preview-content-height, 340px);
  border: 0;
  border-radius: 3px 3px 10px 10px;
  background: transparent;
}
:global(body.dark-mode .home-page-preview) {
  --preview-tint: rgba(32, 32, 34, .72);
  --preview-solid: #252527;
  --preview-ink: #eee;
  --preview-muted: #bcbcbc;
  --preview-edge: rgba(255, 255, 255, .2);
  --preview-outline: rgba(0, 0, 0, .24);
  --preview-hover: rgba(255, 255, 255, .08);
}
.home-page-preview-enter-active { transition: opacity 180ms ease, transform 240ms cubic-bezier(.22, 1, .36, 1); }
.home-page-preview-leave-active { transition: opacity 140ms ease, transform 160ms ease; }
.home-page-preview-enter-from,
.home-page-preview-leave-to { opacity: 0; transform: translateY(var(--preview-offset, 5px)) scale(.98); }
@media (prefers-reduced-transparency: reduce), (prefers-contrast: more) {
  .home-page-preview { background: var(--preview-solid); }
  .home-page-preview::before { display: none; }
}
@media (prefers-contrast: more) {
  .home-page-preview { border-color: var(--preview-muted); }
}
@media (prefers-reduced-motion: reduce) {
  .home-page-preview-enter-active, .home-page-preview-leave-active { transition: opacity 120ms ease; }
  .home-page-preview-enter-from, .home-page-preview-leave-to, .home-page-preview-heading a:hover svg { transform: none; }
  .home-page-preview-heading svg { transition: none; }
}
</style>
